import React, { useState, useEffect } from "react";
import {
    Grid,
    TextField,
    Button,
    Typography,
    CircularProgress,
    IconButton,
    InputAdornment,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Visibility, VisibilityOff, Send as SendIcon } from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../../services/api";

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(2),
    },
    sectionTitle: {
        fontWeight: 700,
        fontSize: "1.1rem",
        marginBottom: 4,
    },
    sectionSubtitle: {
        color: theme.palette.text.secondary,
        fontSize: "0.85rem",
        marginBottom: 20,
    },
    field: {
        marginBottom: 16,
    },
    actions: {
        display: "flex",
        gap: 12,
        marginTop: 20,
        flexWrap: "wrap",
    },
}));

const EmailSettings = ({ settings, user }) => {
    const classes = useStyles();
    const [loading, setLoading] = useState(false);
    const [testing, setTesting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [testEmail, setTestEmail] = useState("");

    const [mailHost, setMailHost] = useState("");
    const [mailPort, setMailPort] = useState("587");
    const [mailUser, setMailUser] = useState("");
    const [mailPass, setMailPass] = useState("");
    const [mailFrom, setMailFrom] = useState("");

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            const { data } = await api.get("/settings");
            const getVal = (key) => {
                const s = data?.find((s) => s.key === key);
                return s?.value || "";
            };
            setMailHost(getVal("mailHost"));
            setMailPort(getVal("mailPort") || "587");
            setMailUser(getVal("mailUser"));
            setMailPass(getVal("mailPass"));
            setMailFrom(getVal("mailFrom"));
        } catch (err) {
            console.error("Erro ao carregar configurações de email:", err);
        }
    };

    const handleSave = async () => {
        if (!mailHost || !mailUser || !mailPass) {
            toast.warn("Preencha pelo menos Host, Usuário e Senha.");
            return;
        }

        setLoading(true);
        try {
            // 1. Validar conexão SMTP antes de salvar
            await api.post("/settings/verify-smtp", {
                host: mailHost,
                port: mailPort,
                user: mailUser,
                pass: mailPass,
            });

            // 2. Salvar configurações
            const entries = [
                { key: "mailHost", value: mailHost },
                { key: "mailPort", value: mailPort },
                { key: "mailUser", value: mailUser },
                { key: "mailPass", value: mailPass },
                { key: "mailFrom", value: mailFrom },
            ];

            for (const entry of entries) {
                await api.put(`/settings/${entry.key}`, { value: entry.value });
            }

            toast.success("✅ Conexão SMTP validada e configurações salvas!");
        } catch (err) {
            const msg = err?.response?.data?.error || "Erro ao validar/salvar configurações.";
            toast.error(msg);
        }
        setLoading(false);
    };

    const handleTestEmail = async () => {
        if (!testEmail) {
            toast.warn("Informe um email para teste.");
            return;
        }
        setTesting(true);
        try {
            await api.post("/settings/test-email", { to: testEmail });
            toast.success("Email de teste enviado! Verifique sua caixa de entrada.");
        } catch (err) {
            const msg = err?.response?.data?.error || "Erro ao enviar email de teste.";
            toast.error(msg);
        }
        setTesting(false);
    };

    return (
        <div className={classes.root}>
            <Typography className={classes.sectionTitle}>
                📧 Configurações de Email (SMTP)
            </Typography>
            <Typography className={classes.sectionSubtitle}>
                Configure o servidor SMTP para envio de emails do sistema (recuperação de senha, notificações, etc.)
            </Typography>

            <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                    <TextField
                        label="Servidor SMTP (Host)"
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={mailHost}
                        onChange={(e) => setMailHost(e.target.value)}
                        placeholder="smtp.gmail.com"
                        className={classes.field}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <TextField
                        label="Porta"
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={mailPort}
                        onChange={(e) => setMailPort(e.target.value)}
                        placeholder="587"
                        className={classes.field}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        label="Usuário / Email"
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={mailUser}
                        onChange={(e) => setMailUser(e.target.value)}
                        placeholder="seuemail@gmail.com"
                        className={classes.field}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        label="Senha / App Password"
                        variant="outlined"
                        fullWidth
                        size="small"
                        type={showPassword ? "text" : "password"}
                        value={mailPass}
                        onChange={(e) => setMailPass(e.target.value)}
                        placeholder="••••••••"
                        className={classes.field}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowPassword((s) => !s)}
                                        edge="end"
                                        size="small"
                                    >
                                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        label="Remetente (From)"
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={mailFrom}
                        onChange={(e) => setMailFrom(e.target.value)}
                        placeholder="Sistema <noreply@suaempresa.com>"
                        className={classes.field}
                    />
                </Grid>
            </Grid>

            <div className={classes.actions}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={loading}
                    style={{ textTransform: "none", borderRadius: 8, fontWeight: 600 }}
                >
                    {loading ? <CircularProgress size={20} /> : "Salvar Configurações"}
                </Button>
            </div>

            <Typography className={classes.sectionTitle} style={{ marginTop: 32 }}>
                🧪 Testar Envio
            </Typography>
            <Typography className={classes.sectionSubtitle}>
                Envie um email de teste para verificar se as configurações estão corretas.
            </Typography>

            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                    <TextField
                        label="Email para teste"
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                        placeholder="teste@exemplo.com"
                        type="email"
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleTestEmail}
                        disabled={testing}
                        startIcon={testing ? <CircularProgress size={16} /> : <SendIcon />}
                        style={{ textTransform: "none", borderRadius: 8, fontWeight: 600 }}
                    >
                        {testing ? "Enviando..." : "Enviar Teste"}
                    </Button>
                </Grid>
            </Grid>
        </div>
    );
};

export default EmailSettings;
